import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Database, FileText, HardDrive, KeyRound, Settings, Users } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import type { DatabaseRecord } from "@hostdock/types";
import { cn, CopyButton, EngineLogo, ErrorState, formatBytes, formatRelative, Skeleton } from "@hostdock/ui";
import { api } from "../api";
import {
  actionsCell, Cell, EmptyCard, FilterTabs, InfoCard, InfoRow, PageHero, PickWebsiteButton, Pill, rowPrimaryClass, SearchBox, StatCard, tableCell, TableShell, Toolbar,
} from "../components/page-kit";
import { DOCS_HREF } from "../layout";

const externalDocs = /^https?:\/\//.test(DOCS_HREF);
const ENGINES = ["MARIADB", "POSTGRESQL", "MONGODB"] as const;
const ENGINE_LABEL: Record<DatabaseRecord["engine"], string> = { MARIADB: "MariaDB", POSTGRESQL: "PostgreSQL", MONGODB: "MongoDB" };
const ENGINE_TILE: Record<DatabaseRecord["engine"], string> = { MARIADB: "bg-[#e6eef7]", POSTGRESQL: "bg-[#e5edf8]", MONGODB: "bg-[#e1f5e6]" };
type Filter = "all" | DatabaseRecord["engine"];

function StatusPill({ status }: { status: string }) {
  if (status === "ACTIVE") return <Pill tone="ok" dot>Active</Pill>;
  if (status === "FAILED") return <Pill tone="bad" dot>Failed</Pill>;
  if (["PENDING", "CREATING", "PROVISIONING"].includes(status)) return <Pill tone="wait" dot>Creating</Pill>;
  if (status === "DELETING") return <Pill tone="wait" dot>Deleting</Pill>;
  return <Pill tone="off" dot>{status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, " ")}</Pill>;
}

/** How an app on the same website reaches the database: the local socket, or a local port when there is one. */
const connectionLine = (d: DatabaseRecord) => (d.connection.port ? `${d.connection.host}:${d.connection.port}` : `${d.connection.host} · ${d.connection.socket}`);

export function DatabasesPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");
  const q = useQuery({ queryKey: ["databases"], queryFn: () => api.databases.list({ pageSize: 100 }), refetchInterval: 30_000 });
  const sites = useQuery({ queryKey: ["websites"], queryFn: () => api.websites.list({ pageSize: 100 }), staleTime: 15_000 });
  const usage = useQuery({ queryKey: ["hosting-usage"], queryFn: api.usage, refetchInterval: 60_000 });
  const siteName = useMemo(() => new Map(sites.data?.items.map((w) => [w.id, w.primaryDomain])), [sites.data]);
  const items = q.data?.items;
  const limit = usage.data?.subscriptions.reduce((a, s) => a + s.limits.databases, 0);
  const summary = useMemo(() => {
    if (!items) return null;
    const measured = items.filter((d) => d.sizeBytes != null);
    const count = (e: Filter) => items.filter((d) => e === "all" || d.engine === e).length;
    return {
      total: items.length,
      active: items.filter((d) => d.status === "ACTIVE").length,
      storage: measured.length ? measured.reduce((a, d) => a + (d.sizeBytes ?? 0), 0) : null,
      users: items.reduce((a, d) => a + d.users.length, 0),
      count,
    };
  }, [items]);
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items?.filter((d) => (filter === "all" || d.engine === filter) && (!term || d.name.toLowerCase().includes(term) || d.users.some((u) => u.username.toLowerCase().includes(term))));
  }, [items, filter, search]);
  const engines = ENGINES.filter((e) => summary?.count(e));

  return (
    <div className="flex flex-col lg:px-[13px]">
      <PageHero
        icon={Database}
        title="Databases"
        description="The databases of your websites, their users and how to connect to them."
        action={<PickWebsiteButton label="Create database" question="Create it for which website?" tab="databases" sites={sites.data?.items} error={sites.error} />}
      />

      {items && items.length > 0 && summary && (
        <>
          <div className="mt-[10px] grid grid-cols-2 gap-[11px] xl:grid-cols-4">
            <StatCard icon={Database} tile="teal" label="Databases" value={summary.total} sub={limit ? `${summary.total} of ${limit} in your plan` : undefined} />
            <StatCard icon={CheckCircle2} tile="green" label="Active" value={summary.active} sub={summary.total ? `${Math.round((summary.active / summary.total) * 100)}% ready to use` : undefined} />
            <StatCard icon={Users} tile="blue" label="Database users" value={summary.users} sub="With access to a database" />
            <StatCard icon={HardDrive} tile="orange" label="Storage used" value={summary.storage == null ? "Unavailable" : formatBytes(summary.storage)} sub={summary.storage == null ? "Not measured yet" : "Measured on the server"} />
          </div>
          <Toolbar>
            <FilterTabs<Filter> label="Filter by engine" value={filter} onChange={setFilter} items={[["all", `All (${summary.total})`], ...engines.map((e) => [e, `${ENGINE_LABEL[e]} (${summary.count(e)})`] as const)]} />
            <SearchBox value={search} onChange={setSearch} placeholder="Search databases or users..." />
          </Toolbar>
        </>
      )}

      <div className="mt-[15px]">
        {q.error ? (
          <div className="hd-card p-6"><ErrorState error={q.error} onRetry={() => void q.refetch()} /></div>
        ) : q.isPending ? (
          <Skeleton className="h-40 w-full rounded-xl" />
        ) : !items || items.length === 0 ? (
          <EmptyCard icon={Database} title="No databases yet" body="Each database belongs to one of your websites. Use Create database to add one; HostDock creates its first user and password for you." />
        ) : rows && rows.length === 0 ? (
          <EmptyCard title="No matching databases" body="Try a different search or filter." />
        ) : (
          <DatabaseTable rows={rows ?? []} siteName={siteName} onManage={(d) => d.websiteId && navigate(`/websites/${d.websiteId}?tab=databases`)} />
        )}
      </div>

      <InfoRow>
        <InfoCard
          to="/websites"
          icon={KeyRound}
          tile="bg-[#d5f4f6] text-[#0a8ea0]"
          className="bg-[#eef9fb] dark:bg-surface-2"
          title="Connect from your website"
          body="Apps on the same website reach their database on localhost. Passwords are never shown again after creation; reset one from the website's Databases tab when you need it."
        />
        <InfoCard to={DOCS_HREF} external={externalDocs} icon={FileText} tile="bg-[#d9eafe] text-[#2f6fd6]" className="bg-[#eff5fc] dark:bg-surface-2" title="Need help with databases?" body="Learn how to create databases and users, import a backup, and open phpMyAdmin from your website." />
      </InfoRow>
    </div>
  );
}

function DatabaseTable({ rows, siteName, onManage }: { rows: DatabaseRecord[]; siteName: Map<string, string>; onManage(d: DatabaseRecord): void }) {
  const cols = "xl:grid xl:grid-cols-[230px_112px_164px_112px_minmax(0,1fr)_84px_116px] xl:items-center";
  return (
    <TableShell label="Databases" cols={cols} headers={["Database", "Engine", "Users", "Status", "Connection", "Size", "Actions"]}>
      {rows.map((d) => {
        const site = d.websiteId ? siteName.get(d.websiteId) : undefined;
        return (
          <li key={d.id} role="row" aria-label={d.name} className={cn(cols, "flex flex-col gap-3 py-4 text-[12.5px] text-fg xl:min-h-[71px] xl:gap-0 xl:py-[11px]")}>
            <div role="cell" className="flex min-w-0 items-center gap-[14px] xl:pr-3">
              <span className={cn("flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-xl dark:bg-surface-3", ENGINE_TILE[d.engine])}><EngineLogo engine={d.engine} size="md" className="!bg-transparent !ring-0" /></span>
              <div className="min-w-0 leading-tight">
                <span className="block truncate font-mono text-[13.5px] font-semibold" title={d.name}>{d.name}</span>
                {site ? <Link to={`/websites/${d.websiteId}`} className="mt-[4px] block truncate text-[11.5px] text-muted hover:text-accent">on {site}</Link> : <span className="mt-[4px] block text-[11.5px] text-muted">Created {formatRelative(d.createdAt)}</span>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:contents">
              <Cell label="Engine" className={tableCell}><span>{ENGINE_LABEL[d.engine]}</span></Cell>
              <Cell label="Users" className={tableCell}>
                {d.users.length === 0 ? <span className="text-muted">None</span> : (
                  <>
                    <span className="max-w-full truncate font-mono text-[12px]" title={d.users.map((u) => u.username).join(", ")}>{d.users[0]!.username}</span>
                    <span className="text-[11px] text-subtle">{d.users.length > 1 ? `+${d.users.length - 1} more` : d.users[0]!.privileges === "READ_ONLY" ? "Read only" : "Full access"}</span>
                  </>
                )}
              </Cell>
              <Cell label="Status" className={tableCell}>
                <StatusPill status={d.status} />
                {d.statusReason && d.status !== "ACTIVE" && <span className="max-w-full truncate text-[11px] text-danger" title={d.statusReason}>{d.statusReason}</span>}
              </Cell>
              <Cell label="Connection" className={cn(tableCell, "col-span-2 sm:col-span-4 xl:col-span-1")}>
                <span className="flex max-w-full min-w-0 items-center gap-2">
                  <span className="flex min-w-0 flex-col leading-[16px]" title={connectionLine(d)}>
                    <span className="truncate font-mono text-[12px]">{d.connection.port ? `${d.connection.host}:${d.connection.port}` : d.connection.host}</span>
                    {!d.connection.port && <span className="truncate font-mono text-[11px] text-subtle">{d.connection.socket}</span>}
                  </span>
                  <span className="shrink-0 [&_button]:h-7 [&_button]:px-2 [&_button]:text-[11.5px]"><CopyButton value={connectionLine(d)} label="Copy" /></span>
                </span>
              </Cell>
              <Cell label="Size" className={tableCell}><span className="tabular-nums">{d.sizeBytes != null ? formatBytes(d.sizeBytes) : "—"}</span></Cell>
            </div>
            <div role="cell" className={actionsCell}>
              <button type="button" onClick={() => onManage(d)} disabled={!d.websiteId} className={cn(rowPrimaryClass, "xl:w-[100px] disabled:opacity-50")}><Settings size={15} className="shrink-0" />Manage</button>
            </div>
          </li>
        );
      })}
    </TableShell>
  );
}
